import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import api from '../api/axios';

const Concentrese = () => {
    const { user } = useAuth();
    const { config } = useConfig();

    const FTP_BASE = 'https://ftp.pcnpublicidad.com/boehringer/concentrese';

    // 8 parejas (16 cartas en total) usando las imágenes exactas del FTP
    const CARD_ITEMS = [
        { id: 1, name: 'Actilyse', img: `${FTP_BASE}/actilyse.jpg` },
        { id: 2, name: 'Somos', img: `${FTP_BASE}/comos.jpg` },
        { id: 3, name: 'Ofev', img: `${FTP_BASE}/ofev.jpg` },
        { id: 4, name: 'Logo Boehringer Ingelheim', img: `${FTP_BASE}/logo%20BI.jpg` },
        { id: 5, name: 'Jardiance', img: `${FTP_BASE}/jardiance.jpg` },
        { id: 6, name: 'Life Forward', img: `${FTP_BASE}/life_forward.jpg` },
        { id: 7, name: 'F1', img: `${FTP_BASE}/F1.jpg` },
        { id: 8, name: 'Metalyse', img: `${FTP_BASE}/metalyse.jpg` },
    ];

    const [cards, setCards] = useState([]);
    const [flippedCards, setFlippedCards] = useState([]);
    const [matchedPairs, setMatchedPairs] = useState([]);
    const [isLockGrid, setIsLockGrid] = useState(false);
    const [gameStatus, setGameStatus] = useState('idle'); // 'idle' | 'playing' | 'won' | 'lost'
    const [timeElapsed, setTimeElapsed] = useState(0); // Conteo progresivo en segundos
    const [moves, setMoves] = useState(0);
    const [earnedPoints, setEarnedPoints] = useState(0);
    const [pointsRegisterMsg, setPointsRegisterMsg] = useState('');
    const [isRulesModalOpen, setIsRulesModalOpen] = useState(true);

    // Puntuación por Tiempo exactos:
    // <= 25s: 40 pts
    // <= 40s: 30 pts
    // > 45s (y 41s-45s): 20 pts
    const calculatePoints = (seconds) => {
        if (seconds <= 25) return 40;
        if (seconds <= 40) return 30;
        return 20;
    };

    const formatTime = (totalSeconds) => {
        const mins = Math.floor(totalSeconds / 60);
        const secs = totalSeconds % 60;
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    };

    const startNewGame = () => {
        const duplicated = [...CARD_ITEMS, ...CARD_ITEMS].map((item, index) => ({
            ...item,
            uniqueId: index,
        }));

        for (let i = duplicated.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [duplicated[i], duplicated[j]] = [duplicated[j], duplicated[i]];
        }

        setCards(duplicated);
        setFlippedCards([]);
        setMatchedPairs([]);
        setIsLockGrid(false);
        setTimeElapsed(0);
        setMoves(0);
        setEarnedPoints(0);
        setPointsRegisterMsg('');
        setGameStatus('playing');
    };

    useEffect(() => {
        startNewGame();
    }, []);

    // Conteo progresivo del tiempo
    useEffect(() => {
        let timer;
        if (gameStatus === 'playing') {
            timer = setInterval(() => {
                setTimeElapsed(prev => prev + 1);
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [gameStatus]);

    const handleCardClick = (index) => {
        if (isLockGrid || gameStatus !== 'playing') return;
        if (flippedCards.includes(index) || matchedPairs.includes(cards[index].id)) return;

        const newFlipped = [...flippedCards, index];
        setFlippedCards(newFlipped);

        if (newFlipped.length === 2) {
            setMoves(m => m + 1);
            setIsLockGrid(true);
            const [firstIdx, secondIdx] = newFlipped;

            if (cards[firstIdx].id === cards[secondIdx].id) {
                setMatchedPairs(prev => {
                    const updated = [...prev, cards[firstIdx].id];
                    if (updated.length === CARD_ITEMS.length) {
                        const finalPoints = calculatePoints(timeElapsed);
                        setEarnedPoints(finalPoints);
                        setGameStatus('won');

                        // Registrar en la BD automáticamente asignando la estación configurada en admin
                        const configuredStationId = config?.pageDesigns?.concentrese?.stationId;

                        console.log("=== DEBUG CONCÉNTRECE GAME WON ===");
                        console.log("Config global:", config);
                        console.log("pageDesigns concentrese:", config?.pageDesigns?.concentrese);
                        console.log("configuredStationId:", configuredStationId);
                        console.log("User data:", user);
                        console.log("Game stats - finalPoints:", finalPoints, "timeElapsed:", timeElapsed);

                        try {
                            api.post('/sessions/log', {
                                page_name: 'concentrese',
                                action_type: 'game_won',
                                user_id: user?.id,
                                time_seconds: timeElapsed,
                                points: finalPoints
                            });

                            if (configuredStationId) {
                                const stationIdPayload = !isNaN(configuredStationId) ? parseInt(configuredStationId, 10) : configuredStationId;
                                const payload = {
                                    stationId: stationIdPayload,
                                    skillPoints: finalPoints,
                                    activityName: 'Concéntrese'
                                };
                                console.log("Enviando a /points/activity-register payload:", payload);

                                api.post('/points/activity-register', payload).then(res => {
                                    console.log("Respuesta de /points/activity-register:", res.data);
                                    setPointsRegisterMsg(res.data.message || '¡Puntos registrados exitosamente!');
                                }).catch(err => {
                                    console.error("Error al guardar puntos de la estación:", err);
                                    if (err.response) {
                                        console.error("Detalles de respuesta del servidor (Status " + err.response.status + "):", err.response.data);
                                    }
                                    if (err.response && err.response.data && err.response.data.message) {
                                        setPointsRegisterMsg(err.response.data.message);
                                    } else {
                                        setPointsRegisterMsg('Ocurrió un error al registrar los puntos.');
                                    }
                                });
                            } else {
                                console.warn("ADVERTENCIA: No se encontró un stationId configurado en config.pageDesigns.concentrese.stationId");
                                setPointsRegisterMsg("Nota: No se ha asignado una estación en las configuraciones para acumular puntos.");
                            }
                        } catch (err) {
                            console.error("Error logging game session", err);
                        }
                    }
                    return updated;
                });
                setFlippedCards([]);
                setIsLockGrid(false);
            } else {
                setTimeout(() => {
                    setFlippedCards([]);
                    setIsLockGrid(false);
                }, 800);
            }
        }
    };

    return (
        <div
            className="min-h-screen bg-[#07241c] text-white flex flex-col items-center justify-start p-3 sm:p-4 gap-2 sm:gap-4 relative overflow-hidden font-sans bg-cover bg-center"
            style={{ backgroundImage: `url(${FTP_BASE}/fondo.png)` }}
        >
            {/* Modal de Reglas de Juego */}
            {isRulesModalOpen && createPortal(
                <div className="fixed inset-0 bg-black/85 backdrop-blur-md z-[99999] flex items-center justify-center p-4 animate-fadeIn">
                    <div className="bg-[#0b382c] border-2 border-[#00e676] rounded-3xl p-6 sm:p-8 max-w-sm w-full text-center space-y-5 shadow-2xl">
                        <div className="text-4xl">📋</div>
                        <h2 className="text-xl font-black text-[#00e676] uppercase tracking-wider">
                            REGLAS DE JUEGO & PUNTUACIÓN
                        </h2>
                        <p className="text-xs text-slate-200 leading-relaxed font-medium">
                            Encuentre las parejas de cartas en el menor tiempo posible. Los puntos de destreza se otorgan según el tiempo registrado:
                        </p>
                        <div className="bg-[#07241c] border border-[#00e676]/30 p-3 rounded-2xl text-xs font-bold text-slate-200 flex flex-col gap-2 text-left shadow-inner">
                            <div className="flex justify-between items-center bg-[#0b382c] p-2.5 rounded-xl border border-[#00e676]/20">
                                <span>Hasta 25 segundos:</span>
                                <span className="font-black text-[#00e676]">40 pts</span>
                            </div>
                            <div className="flex justify-between items-center bg-[#0b382c] p-2.5 rounded-xl border border-[#00e676]/20">
                                <span>Hasta 40 segundos:</span>
                                <span className="font-black text-[#00e676]">30 pts</span>
                            </div>
                            <div className="flex justify-between items-center bg-[#0b382c] p-2.5 rounded-xl border border-[#00e676]/20">
                                <span>Más de 45 segundos:</span>
                                <span className="font-black text-amber-300">20 pts</span>
                            </div>
                        </div>

                        <p className="text-xs font-bold text-[#00e676] bg-[#07241c] p-2.5 rounded-xl border border-[#00e676]/30">
                            💡 Al finalizar haz click en el menú y vuelve al inicio.
                        </p>

                        <button
                            onClick={() => setIsRulesModalOpen(false)}
                            className="w-full bg-[#00e676] text-[#07241c] py-3.5 px-6 rounded-2xl font-black uppercase tracking-widest text-xs shadow-lg hover:scale-105 active:scale-95 transition-all"
                        >
                            ENTENDIDO / JUGAR
                        </button>
                    </div>
                </div>,
                document.body
            )}

            {/* Header / Banner de Actividad */}
            <div className="w-full max-w-lg flex flex-col items-center gap-1.5 pt-1 z-10">
                {/* Insignias superiores */}
                <div className="w-full flex justify-between items-center px-2">
                    <img src={`${FTP_BASE}/concentrese.png`} alt="CONCÉNTRECE" className="h-8 sm:h-12 object-contain" />
                    <button
                        onClick={() => setIsRulesModalOpen(true)}
                        className="bg-[#0b382c] border border-[#00e676]/40 text-[#00e676] px-3 py-1 rounded-full text-xs font-black tracking-wider uppercase hover:bg-[#00e676] hover:text-[#07241c] transition-all shadow-md flex items-center gap-1"
                    >
                        📋 Reglas de Juego
                    </button>
                </div>

                {/* Título Principal */}
                <div className="text-center mt-0.5 flex justify-center">
                    <img src={`${FTP_BASE}/titulo.png`} alt="ENCIENDE TU MEMORIA Y ACELERA" className="w-full max-w-xs sm:max-w-md object-contain" />
                </div>

                {/* Banner verde explicativo */}
                <div className="w-full flex justify-center">
                    <img src={`${FTP_BASE}/enunciado.png`} alt="Encuentra las parejas..." className="w-full max-w-md object-contain" />
                </div>
            </div>

            {/* Marcador de Tiempo Progresivo e Intentos */}
            <div className="w-full max-w-md flex justify-between items-center px-2 my-0 text-xs sm:text-sm font-bold z-10">
                <div className="bg-[#0b382c]/90 border border-[#00e676]/40 px-3 py-1 rounded-lg flex items-center gap-2 backdrop-blur-sm">
                    <span>⏱️ TIEMPO:</span>
                    <span className={`text-base font-black ${timeElapsed >= 60 ? 'text-amber-400 animate-pulse' : 'text-[#00e676]'}`}>
                        {formatTime(timeElapsed)}
                    </span>
                </div>
                <div className="bg-[#0b382c]/90 border border-[#00e676]/40 px-3 py-1 rounded-lg flex items-center gap-2 backdrop-blur-sm">
                    <span>🔄 INTENTOS:</span>
                    <span className="text-base font-black text-[#00e676]">{moves}</span>
                </div>
            </div>

            {/* Tablero de Juego (4x4 Grid) */}
            <div className="w-full max-w-md bg-[#00e676] p-2.5 sm:p-4 rounded-2xl shadow-2xl z-10 border-4 border-white/30 relative">
                <div className="grid grid-cols-4 gap-2 sm:gap-3">
                    {cards.map((card, index) => {
                        const isFlipped = flippedCards.includes(index) || matchedPairs.includes(card.id);
                        return (
                            <div
                                key={index}
                                onClick={() => handleCardClick(index)}
                                className={`aspect-square rounded-lg cursor-pointer transition-all duration-300 transform select-none relative flex items-center justify-center overflow-hidden shadow-md ${isFlipped ? 'bg-white border-2 border-[#00e676]' : 'bg-[#072d23] hover:scale-[1.03] active:scale-95 border-2 border-[#00e676]/50'
                                    }`}
                            >
                                {isFlipped ? (
                                    <img
                                        src={card.img}
                                        alt={card.name}
                                        className="w-full h-full object-contain p-1 animate-fadeIn"
                                    />
                                ) : (
                                    /* Ilustración trasera tapada exacta */
                                    <img
                                        src={`${FTP_BASE}/carro_tapado.jpg`}
                                        alt="Tapado"
                                        className="w-full h-full object-cover"
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Modal / Feedback de Fin de Juego */}
            {gameStatus !== 'playing' && gameStatus !== 'idle' && createPortal(
                <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[99999] flex items-center justify-center p-4 animate-fadeIn">
                    <div className="bg-[#0b382c] border-2 border-[#00e676] rounded-3xl p-6 sm:p-8 max-w-sm w-full text-center space-y-5 shadow-2xl">
                        {gameStatus === 'won' ? (
                            <>
                                <div className="text-5xl">🏆</div>
                                <h2 className="text-2xl font-black text-[#00e676] uppercase tracking-wider">¡FELICITACIONES!</h2>
                                <p className="text-sm font-bold text-white leading-relaxed">
                                    ¡Has encendido tu memoria y superado el reto!
                                </p>
                                <div className="bg-[#07241c] p-3 rounded-xl border border-[#00e676]/30 space-y-1">
                                    <p className="text-xs text-slate-300">
                                        Tiempo completado: <span className="font-bold text-[#00e676]">{formatTime(timeElapsed)}</span>
                                    </p>
                                    <p className="text-xs text-slate-300">
                                        Intentos realizados: <span className="font-bold text-[#00e676]">{moves}</span>
                                    </p>
                                    <p className="text-sm font-black text-amber-400 pt-1">
                                        ⭐ Puntos Obtenidos: {earnedPoints} pts
                                    </p>
                                    {pointsRegisterMsg && (
                                        <p className="text-[11px] text-[#00e676] font-bold pt-1 border-t border-[#00e676]/20">
                                            {pointsRegisterMsg}
                                        </p>
                                    )}
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="text-5xl">⏰</div>
                                <h2 className="text-2xl font-black text-red-400 uppercase tracking-wider">¡TIEMPO LÍMITE ALCANZADO!</h2>
                                <p className="text-sm font-bold text-white leading-relaxed">
                                    Has superado el tiempo máximo de 1:10 min. ¡Vuelve a intentarlo para sumar puntos!
                                </p>
                            </>
                        )}

                        <p className="text-xs font-bold text-[#00e676] bg-[#07241c] p-2.5 rounded-xl border border-[#00e676]/30">
                            💡 Al finalizar haz click en el menú y vuelve al inicio.
                        </p>

                        <button
                            onClick={startNewGame}
                            className="w-full bg-[#00e676] text-[#07241c] py-3.5 px-6 rounded-xl font-black uppercase tracking-widest text-sm shadow-lg hover:scale-105 active:scale-95 transition-all"
                        >
                            JUGAR DE NUEVO
                        </button>
                    </div>
                </div>,
                document.body
            )}

        </div>
    );
};

export default Concentrese;
